<template>
  <div class="grid gap-4">
    <div class="border rounded px-3 py-2 cursor-pointer flex justify-between items-center bg-white" @click="useUpdateQuery({ locationModal: true })">
      <AtomRestaurantLocation
        :name="city?.name"
        :address="$t('shared.restaurants', city?.restaurants)" />
      <div class="text-sm text-[#888]">
        {{ $t('shared.changeCity') }}
      </div>
    </div>
    <div v-if="city.restaurants < 3">
      <AtomInfoBox
        :description="city.restaurants < 3 
          ? $t('resReq.form.forbidden', { city: city.name })
          : $t('resReq.form.disclaimer', { city: city.name })" />
    </div>
    <div class="grid gap-4">
      <div class="grid grid-cols-4 gap-4">
        <div class="grid gap-1 col-span-2">
          <div class="text-xs" v-html="$t('shared.label.date')" />
          <input
            class="input w-full"
            :class="{ 'border-red-500': $dayjs(fat.date).isBefore($dayjs(), 'day') || !$dayjs(fat.date, 'YYYY-MM-DD', true).isValid() }"
            v-model="fat.date"
            type="text"
          />
        </div>
        <div class="grid gap-1">
          <div class="text-xs" v-html="$t('shared.label.time')" />
          <select class="input" v-model="formData.time">
            <option v-for="time in availableTimes"
              :key="time"
              :value="time"
              :selected="time === formData.time">
              {{ time }}
            </option>
          </select>
        </div>
        <div class="grid gap-1">
          <div class="text-xs" v-html="$t('shared.label.amount')" />
          <input
            class="input w-full"
            :class="{ 'border-red-500': v$.amount.$error }"
            v-model="formData.amount"
            type="number"
            id="amount"
            name="amount"
            @change="v$.amount.$touch"
            min="9"
            max="150"
          />
        </div>
      </div>
      <div class="grid grid-cols-2 gap-4">
        <div class="grid gap-1">
          <div class="text-xs" v-html="$t('shared.label.firstName')" />
          <input
            class="input w-full"
            :class="{ 'border-red-500': v$.firstName.$error }"
            v-model="formData.firstName"
            type="text"
            id="firstName"
            name="firstName"
            @change="v$.firstName.$touch"
          />
        </div>
        <div class="grid gap-1">
          <div class="text-xs" v-html="$t('shared.label.lastName')" />
          <input
            class="input w-full"
            :class="{ 'border-red-500': v$.lastName.$error }"
            v-model="formData.lastName"
            type="text"
            id="lastName"
            name="lastName"
            @change="v$.lastName.$touch"
          />
        </div>
      </div>
      <div class="grid gap-1">
        <div class="text-xs" v-html="$t('shared.label.email')" />
        <input
          class="input"
          :class="{ 'border-red-500': v$.email.$error }"
          v-model="formData.email"
          type="email"
          id="email"
          name="email"
          @change="v$.email.$touch"
        />
      </div>
      <div class="grid gap-1">
        <div class="text-xs" v-html="$t('shared.label.phone')" />
        <input
          class="input"
          :class="{ 'border-red-500': v$.phone.$error }"
          v-model="formData.phone"
          type="tel"
          id="phone"
          name="phone"
          @change="v$.phone.$touch"
        />
      </div>
      <div class="grid gap-1">
        <div class="text-xs" v-html="$t('shared.label.comment')" />
        <input class="input" type="text" v-model="formData.comment" />
      </div>
      <button class="button-coral text-sm" @click="submitForm" :disabled="isPosting || (city.restaurants < 3 && city.id !== 949629)">{{ $t('resReq.form.button') }}</button>
      <div class="text-sm text-red-600 text-center" v-if="hasError">{{ $t('shared.genericError') }}</div>
    </div>
  </div>
</template>

<script setup>
import { useVuelidate } from '@vuelidate/core'
import { required, email, between, minLength, maxLength } from '@vuelidate/validators'

const route = useRoute()
const router = useRouter()
const dayjs = useDayjs()
const app = useAppStore()
const fat = useFatStore()
const user = useUserStore()
const hasError = ref(false)
const isPosting = ref(false)
const resReq = useState('resReqForm')

// Get city from path
const city = computed(() => fat.city?.slug ? fat.city : app.cities[0])

// Generate time slots from 09:00 to 22:45, based on selected date
const availableTimes = computed(() => {
  const timeSlots = []
  const startTime = dayjs().hour(6).minute(0).second(0)
  const endTime = dayjs().hour(22).minute(30).second(0)

  let currentTime = startTime

  while (currentTime.isBefore(endTime) || currentTime.isSame(endTime)) {
    timeSlots.push(currentTime.format('HH:mm'))
    currentTime = currentTime.add(30, 'minute')
  }

  // Get the selected date from the form data
  const selectedDate = dayjs(fat.date)
  const now = dayjs()

  // If the selected date is today, filter out passed times
  if (selectedDate.isSame(now, 'day')) {
    return timeSlots.filter(time => {
      const timeAsDate = dayjs(`${selectedDate.format('YYYY-MM-DD')} ${time}`);
      return timeAsDate.isAfter(now);
    })
  }

  // If not today, return all time slots
  return timeSlots
})

// Figure out meal type based on time
const mealType = computed(() => {
  const formDate = `${fat.date}`
  const formDateTime = `${formDate} ${formData.value.time}`

  if (dayjs(formDateTime).isBefore(`${formDate} 10:30`)) return 'breakfast'
  if (dayjs(formDateTime).isBefore(`${formDate} 14:30`)) return 'lunch'
  return 'dinner'
})

// Define form rules
const rules = computed(() => {
  return {
    amount: { required, minValue: between(9, 150) },
    email: { required, email },
    firstName: { required, minLength: minLength(2) },
    lastName: { required, minLength: minLength(2) },
    phone: { required, minLength: minLength(8) },
  }
})

// Create form object
const formData = ref({
  amount: '12',
  time: '19:00',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  comment: '',
})

// Use the useVuelidate to run our validations
const v$ = useVuelidate(rules, formData);

// Submit Res Req form
const submitForm = async () => {
  hasError.value = false
  isPosting.value = true
  await useDelay(500)
  v$.value.$validate()

  if (v$.value.$error) {
    isPosting.value = false
  }

  if (!v$.value.$error) {
    $fetch('/reservation-requests', {
      baseURL: useEnv('apiBase'),
      method: 'POST',
      body: {
        cityId: fat.cityId,
        date: fat.date,
        meal: mealType?.value,
        ...formData.value
      },
      onResponse({ response }) {
        resReq.value = response?._data?.data
        router.push({ name: 'res-req', query: { ...route.query, 'id': resReq.value?.id }})
        isPosting.value = false
      },
      onResponseError({ request, response }) {
        hasError.value = true
        isPosting.value = false
      }
    })
  }
}

// Update form with user details
onMounted(() => {
  formData.value = {
    ...formData.value,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    phone: user.phone
  }
})
</script>
